import { Badge, Box, Flex, Text } from '@chakra-ui/react'
import { EventContentArg } from '@fullcalendar/core'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import FullCalendar from '@fullcalendar/react'
import timeGridPlugin from '@fullcalendar/timegrid'
import { useContentWidth, useScreen } from '@hb/react'
import { colors, getTimeString } from '@hb/shared'
import { useMemo, useRef } from 'react'
import { useResizeObserver } from '../../hooks'
import { NewVisitModal } from '../../charting/Visits/NewVisitModal'
import { useVisitsCalendar } from './context'
import { EventOverlay } from './EventsOverlay'
import './styles.css'
import { VisitsCalendarProvider } from './VisitsCalendarProvider'

const VisitEventView = ({ event }: EventContentArg) => {
  const { title, start, end } = event
  const { type } = event.extendedProps
  return (
    <Box px={2} py={1} bg={colors.green.hex} borderRadius={3} w="100%" minH="100%">
      <Flex gap={1} w="100%">
        <Text
          color="white"
          fontFamily="Open Sans"
          fontWeight={500}
          fontSize="sm"
          flex={1}
          pt={0.5}
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
          textShadow="1px 1px 3px rgba(0, 0, 0, 0.5)">
          {title}
        </Text>
        {type === 'visit-draft' ? (
          <Box>
            <Badge bg="white" color="gray.600" fontFamily="Open Sans" fontWeight={500}>
              Draft
            </Badge>
          </Box>
        ) : null}
      </Flex>
      <Flex gap={1} w="100%">
        <Text color="white" fontFamily="Open Sans" fontWeight={500} fontSize="sm">
          {start ? getTimeString(start) : ''}
        </Text>
        <Text color="white" fontFamily="Open Sans" fontWeight={500} fontSize="sm">
          - {end ? getTimeString(end) : ''}
        </Text>
      </Flex>
    </Box>
  )
}

const VisitsCalendarContent = () => {
  const containerRef = useRef<HTMLDivElement>(null)
  const containerSize = useResizeObserver(containerRef, 'border')
  const { height } = containerSize
  const { width: screenWidth, height: screenHeight } = useScreen()
  const contentWidth = useContentWidth(screenWidth, 1000)
  const dayWidth = useMemo(() => contentWidth / 7, [contentWidth])

  const { handleDateClick, handleDatesSet, events, handleEventClick } = useVisitsCalendar()
  // const [dates, setDates] = useState<DateSetArg | null>()

  return (
    <Box position="relative" bg="white" borderRadius={4} ref={containerRef} w="100%" h="100%">
      <FullCalendar
        headerToolbar={{
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,timeGridWeek,timeGridDay',
        }}
        buttonText={{
          today: 'Today',
          month: 'Month',
          week: 'Week',
          day: 'Day',
        }}
        height={height}
        initialView="timeGridWeek"
        // width
        eventContent={VisitEventView}
        slotMinWidth={dayWidth}
        dateClick={handleDateClick}
        eventClick={handleEventClick}
        events={events}
        datesSet={handleDatesSet}
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        allDaySlot={false}
        slotMinTime="07:00:00"
        slotMaxTime="20:00:00"
        slotDuration="00:30:00"
        scrollTime="08:00:00"
      />
      <EventOverlay
        containerSize={containerSize}
        screenWidth={screenWidth}
        screenHeight={screenHeight}
      />
    </Box>
  )
}

const CalendarNewVisitModal = () => {
  const { newVisitArgs, onCloseNewVisit } = useVisitsCalendar()
  return (
    <NewVisitModal
      patientId={newVisitArgs?.patientId ?? null}
      practiceId={newVisitArgs?.practiceId ?? null}
      onComplete={onCloseNewVisit}
      onClose={onCloseNewVisit}
      isOpen={!!newVisitArgs}
      date={newVisitArgs?.date ?? null}
    />
  )
}

export const VisitsCalendar = ({ practiceId }: { practiceId: string | null }) => (
  <VisitsCalendarProvider practiceId={practiceId}>
    <VisitsCalendarContent />
    <CalendarNewVisitModal />
  </VisitsCalendarProvider>
)
